import { useState, useEffect } from "react";
import { Calculator, Euro, Home, Ruler, TrendingUp, TrendingDown, Info, Settings, Building2, Edit2, Check, X, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildTimelinesForProgram, TimelineDisplay } from "@/components/ProjectTimeline";

// Coefficients par surface
const COEFFICIENTS_SURFACE = [
  { min: 0, max: 34, coef: 0.40 },
  { min: 35, max: 59, coef: 0.50 },
  { min: 60, max: 89, coef: 0.80 },
  { min: 90, max: 109, coef: 1.00 },
  { min: 110, max: 129, coef: 1.20 },
  { min: 130, max: 1000, coef: 1.30 }
];

// MWh cumac par nombre de sauts de classes
const CEE_PAR_CLASSES = {
  2: { mwhCumac: 723 },
  3: { mwhCumac: 892 },
  4: { mwhCumac: 1138 }
};

// TVA
const TVA = 0.055; // 5.5%

function getRegies(mode, precarite, surfaceNum) {
  if (mode === "lead") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (precarite === "jaune") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (surfaceNum < 70) return { r174: 1000, rMPR: 2000, r174Cum: 1000, rMPRCum: 2000 };
  if (surfaceNum <= 130) return { r174: 1500, rMPR: 2500, r174Cum: 1500, rMPRCum: 2500 };
  return { r174: 2000, rMPR: 3000, r174Cum: 2000, rMPRCum: 3000 };
}

// Types de travaux avec coûts par défaut
const TRAVAUX_DEFAUT = {
  combles: { label: "Isolation combles", materiel: 12, pose: 8, unite: "m²" },
  planchersBas: { label: "Isolation plancher bas", materiel: 12, pose: 10, unite: "m²" },
  rampant: { label: "Rampant", materiel: 0, pose: 50, unite: "m²" },
  murs: { label: "Isolation murs intérieurs", materiel: 0, pose: 50, unite: "m²" },
  fenetres: { label: "Fenêtres", materiel: 450, pose: 0, unite: "unité" },
  ballonHybride: { label: "Ballon Hybride", materiel: 450, pose: 350, unite: "unité" },
  radiateurs: { label: "Radiateurs électriques", materiel: 100, pose: 70, unite: "unité" },
  monoSplit: { label: "Mono Split (1 split)", materiel: 800, pose: 500, unite: "unité" },
  splitSupp: { label: "Split supplémentaire", materiel: 150, pose: 500, unite: "unité" },
  vmcSimple: { label: "VMC Simple flux", materiel: 0, pose: 600, unite: "unité" },
  vmcDouble: { label: "VMC Double flux", materiel: 0, pose: 1950, unite: "unité" },
  vmcDoubleCompact: { label: "VMC Double flux compact", materiel: 0, pose: 700, unite: "unité" },
  thermostat: { label: "Kit Thermostat connecté", materiel: 0, pose: 200, unite: "unité" },
  coutsFixes: { label: "Coûts fixes", materiel: 0, pose: 545, unite: "" }
};

export default function Simulateur174() {
  // Nombre de logements
  const [nombreLogements, setNombreLogements] = useState(1);
  
  // Surface par défaut
  const [surfaceDefaut, setSurfaceDefaut] = useState(135);
  
  // Surfaces individuelles des logements (null = utilise surface par défaut)
  const [surfacesLogements, setSurfacesLogements] = useState([null]);
  
  // Logement en cours d'édition
  const [editingLogement, setEditingLogement] = useState(null);
  const [tempSurface, setTempSurface] = useState("");

  // Paramètres principaux
  const [params, setParams] = useState({
    typeLogement: "Maison",
    typeChauffage: "Elec"
  });

  // Quantités de travaux (par logement)
  const [quantites, setQuantites] = useState({
    combles: 70,
    planchersBas: 70,
    rampant: 0,
    murs: 0,
    fenetres: 2,
    ballonHybride: 0,
    radiateurs: 0,
    monoSplit: 1,
    splitSupp: 0,
    vmcSimple: 0,
    vmcDouble: 0,
    vmcDoubleCompact: 0,
    thermostat: 0,
    coutsFixes: 1
  });

  // Scénario sélectionné
  const [scenario, setScenario] = useState(2);

  // Précarité
  const [precarite174, setPrecarite174] = useState("bleu");
  const isJaune174 = precarite174 === "jaune";

  // Mode coût Lead/Régie
  const [regieMode, setRegieMode] = useState("regie");

  // Régies calculées
  const regies = getRegies(regieMode, precarite174, surfaceDefaut);
  const regie = regies.r174;

  // Valorisation CEE (€/MWh cumac) - éditable
  const [valorisation, setValorisation] = useState(13);

  // Coûts modifiables
  const [couts, setCouts] = useState({
    combles: { materiel: 12, pose: 8 },
    planchersBas: { materiel: 12, pose: 10 },
    rampant: { materiel: 0, pose: 50 },
    murs: { materiel: 0, pose: 50 },
    fenetres: { materiel: 450, pose: 0 },
    ballonHybride: { materiel: 450, pose: 350 },
    radiateurs: { materiel: 100, pose: 70 },
    monoSplit: { materiel: 800, pose: 500 },
    splitSupp: { materiel: 150, pose: 500 },
    vmcSimple: { materiel: 0, pose: 600 },
    vmcDouble: { materiel: 0, pose: 1950 },
    vmcDoubleCompact: { materiel: 0, pose: 700 },
    thermostat: { materiel: 0, pose: 200 },
    coutsFixes: { materiel: 0, pose: 545 }
  });

  // Résultats calculés
  const [resultats, setResultats] = useState(null);
  const [resultatsParLogement, setResultatsParLogement] = useState([]);

  // Mettre à jour les surfaces quand le nombre de logements change
  useEffect(() => {
    setSurfacesLogements(prev => {
      const newSurfaces = [...prev];
      while (newSurfaces.length < nombreLogements) {
        newSurfaces.push(null); // null = surface par défaut
      }
      return newSurfaces.slice(0, nombreLogements);
    });
  }, [nombreLogements]);

  // Calcul automatique
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculerResultats();
  }, [params, quantites, scenario, couts, nombreLogements, surfaceDefaut, surfacesLogements, valorisation, regie, precarite174]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const getCoefficient = (surface) => {
    const coefData = COEFFICIENTS_SURFACE.find(c => surface >= c.min && surface <= c.max);
    return coefData ? coefData.coef : 1.30;
  };

  const getSurfaceLogement = (index) => {
    return surfacesLogements[index] !== null ? surfacesLogements[index] : surfaceDefaut;
  };

  const calculerResultatsPourLogement = (surface) => {
    const coefficient = getCoefficient(surface);
    const ceeData = CEE_PAR_CLASSES[scenario];
    
    // Formule : MWh Cumac × Coefficient surface × Valorisation
    const caTTCBrut = ceeData.mwhCumac * coefficient * valorisation;
    const caTTC = caTTCBrut;
    const caHT = caTTC / (1 + TVA);
    
    let coutTravaux = 0;
    Object.keys(quantites).forEach(key => {
      const qte = quantites[key];
      const cout = couts[key];
      if (qte > 0) {
        coutTravaux += (cout.materiel + cout.pose) * qte;
      }
    });
    coutTravaux += regie;
    
    const margeBrute = caHT - coutTravaux;
    
    return {
      surface,
      coefficient,
      mwhCumac: ceeData.mwhCumac,
      caTTCBrut: Math.round(caTTCBrut * 100) / 100,
      caTTC: Math.round(caTTC * 100) / 100,
      caHT: Math.round(caHT * 100) / 100,
      coutTravaux: Math.round(coutTravaux * 100) / 100,
      margeBrute: Math.round(margeBrute * 100) / 100
    };
  };

  const calculerResultats = () => {
    // Calculer pour chaque logement
    const resultatsLogements = [];
    let totalCaTTC = 0;
    let totalCaHT = 0;
    let totalCoutTravaux = 0;
    let totalMarge = 0;
    let totalMwhCumac = 0;

    for (let i = 0; i < nombreLogements; i++) {
      const surface = getSurfaceLogement(i);
      const res = calculerResultatsPourLogement(surface);
      resultatsLogements.push({ ...res, index: i + 1 });
      
      totalCaTTC += res.caTTC;
      totalCaHT += res.caHT;
      totalCoutTravaux += res.coutTravaux;
      totalMarge += res.margeBrute;
      totalMwhCumac += res.mwhCumac;
    }

    setResultatsParLogement(resultatsLogements);
    setResultats({
      nombreLogements,
      totalCaTTC: Math.round(totalCaTTC * 100) / 100,
      totalCaHT: Math.round(totalCaHT * 100) / 100,
      totalCoutTravaux: Math.round(totalCoutTravaux * 100) / 100,
      totalMarge: Math.round(totalMarge * 100) / 100,
      totalMwhCumac,
      moyenneMarge: Math.round((totalMarge / nombreLogements) * 100) / 100
    });
  };

  const handleQuantiteChange = (key, value) => {
    setQuantites(prev => ({ ...prev, [key]: parseInt(value) || 0 }));
  };

  const handleCoutChange = (key, field, value) => {
    setCouts(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: parseFloat(value) || 0 }
    }));
  };

  const handleNombreLogementsChange = (value) => {
    const num = Math.max(1, Math.min(100, parseInt(value) || 1));
    setNombreLogements(num);
  };

  const startEditingLogement = (index) => {
    setEditingLogement(index);
    setTempSurface(getSurfaceLogement(index).toString());
  };

  const saveLogementSurface = (index) => {
    const newSurface = parseInt(tempSurface) || surfaceDefaut;
    setSurfacesLogements(prev => {
      const newSurfaces = [...prev];
      newSurfaces[index] = newSurface === surfaceDefaut ? null : newSurface;
      return newSurfaces;
    });
    setEditingLogement(null);
  };

  const resetLogementSurface = (index) => {
    setSurfacesLogements(prev => {
      const newSurfaces = [...prev];
      newSurfaces[index] = null;
      return newSurfaces;
    });
    setEditingLogement(null);
  };

  const getCoutTravail = (key) => {
    const qte = quantites[key];
    const cout = couts[key];
    return qte * (cout.materiel + cout.pose);
  };

  // Compter les logements personnalisés
  const logementsPersonnalises = surfacesLogements.filter(s => s !== null).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Calculator className="w-8 h-8 text-primary" />
            Simulateur 174
          </h1>
          <p className="text-muted-foreground mt-1">
            Modèle économique Label - Rénovation énergétique
          </p>
        </div>
      </div>

      <Tabs defaultValue="simulation" className="space-y-6">
        <TabsList className="bg-muted rounded-sm p-1">
          <TabsTrigger value="simulation" className="rounded-sm">
            <Calculator className="w-4 h-4 mr-2" />
            Simulation
          </TabsTrigger>
          <TabsTrigger value="logements" className="rounded-sm">
            <Building2 className="w-4 h-4 mr-2" />
            Logements ({nombreLogements})
          </TabsTrigger>
          <TabsTrigger value="couts" className="rounded-sm">
            <Settings className="w-4 h-4 mr-2" />
            Coûts unitaires
          </TabsTrigger>
        </TabsList>

        <TabsContent value="simulation">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Paramètres */}
            <div className="border border-border rounded-sm bg-card p-6 space-y-5">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
                <Home className="w-5 h-5 text-primary" />
                Paramètres
              </h2>
              
              <div className="space-y-4">
                {/* Nombre de logements */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-sm">
                  <Label className="flex items-center gap-2 text-primary font-semibold">
                    <Building2 className="w-4 h-4" />
                    Nombre de logements
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleNombreLogementsChange(nombreLogements - 1)}
                      disabled={nombreLogements <= 1}
                      className="rounded-sm"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      value={nombreLogements}
                      onChange={(e) => handleNombreLogementsChange(e.target.value)}
                      className="w-20 rounded-sm text-center text-lg font-bold"
                      min="1"
                      max="100"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleNombreLogementsChange(nombreLogements + 1)}
                      disabled={nombreLogements >= 100}
                      className="rounded-sm"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Valorisation CEE */}
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                  <Label className="flex items-center gap-2 text-amber-800 font-semibold">
                    <Euro className="w-4 h-4" />
                    Valorisation CEE (€/MWh cumac)
                  </Label>
                  <Input
                    type="number"
                    value={valorisation}
                    onChange={(e) => setValorisation(parseFloat(e.target.value) || 0)}
                    className="w-full rounded-sm mt-2 text-lg font-bold text-center"
                    step="0.5"
                    min="0"
                    data-testid="valorisation-input"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    Formule : MWh cumac × coefficient × valorisation
                  </p>
                </div>

                {/* Précarité */}
                <div>
                  <Label>Précarité</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    <Button type="button" variant={precarite174 === "bleu" ? "default" : "outline"}
                      onClick={() => { setPrecarite174("bleu"); setValorisation(13); }}
                      className={cn("rounded-sm", precarite174 === "bleu" && "bg-blue-500 hover:bg-blue-600")}
                    >Bleu (Très Modeste)</Button>
                    <Button type="button" variant={precarite174 === "jaune" ? "default" : "outline"}
                      onClick={() => { setPrecarite174("jaune"); setValorisation(8); }}
                      className={cn("rounded-sm", precarite174 === "jaune" && "bg-amber-500 hover:bg-amber-600")}
                    >Jaune (Modeste)</Button>
                  </div>
                  {isJaune174 && <p className="text-xs text-amber-600 mt-1">Jaune : valorisation 8 €</p>}
                </div>

                <div>
                  <Label>Type de logement</Label>
                  <Select value={params.typeLogement} onValueChange={(v) => setParams(p => ({ ...p, typeLogement: v }))}>
                    <SelectTrigger className="rounded-sm mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Maison">Maison</SelectItem>
                      <SelectItem value="Appartement">Appartement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Type de chauffage actuel</Label>
                  <Select value={params.typeChauffage} onValueChange={(v) => setParams(p => ({ ...p, typeChauffage: v }))}>
                    <SelectTrigger className="rounded-sm mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Elec">Électrique</SelectItem>
                      <SelectItem value="Gaz">Gaz</SelectItem>
                      <SelectItem value="Fioul">Fioul</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Ruler className="w-4 h-4" />
                    Surface par défaut (m²)
                  </Label>
                  <Input
                    type="number"
                    value={surfaceDefaut}
                    onChange={(e) => setSurfaceDefaut(parseInt(e.target.value) || 0)}
                    className="rounded-sm mt-1.5"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Coefficient: {getCoefficient(surfaceDefaut)}
                    {logementsPersonnalises > 0 && (
                      <span className="ml-2 text-amber-600">
                        ({logementsPersonnalises} logement{logementsPersonnalises > 1 ? 's' : ''} personnalisé{logementsPersonnalises > 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                </div>

                <div>
                  <Label>Nombre de classes (scénario)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {[2, 3, 4].map(n => (
                      <Button
                        key={n}
                        type="button"
                        variant={scenario === n ? "default" : "outline"}
                        onClick={() => setScenario(n)}
                        className="rounded-sm"
                      >
                        {n} classes
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Travaux par logement */}
            <div className="border border-border rounded-sm bg-card p-6 space-y-5">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
                <Settings className="w-5 h-5 text-primary" />
                Travaux par logement
              </h2>
              
              <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
                {Object.entries(TRAVAUX_DEFAUT).map(([key, travail]) => (
                  <div key={key} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{travail.label}</p>
                      {quantites[key] > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {getCoutTravail(key).toLocaleString('fr-FR')} €/logement
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={quantites[key]}
                        onChange={(e) => handleQuantiteChange(key, e.target.value)}
                        className="w-20 rounded-sm text-center"
                        min="0"
                      />
                      {travail.unite && (
                        <span className="text-xs text-muted-foreground w-10">{travail.unite}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-3 border-t border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mode coût</span>
                    <div className="flex gap-1">
                      <Button type="button" variant={regieMode === "regie" ? "default" : "outline"} onClick={() => setRegieMode("regie")} className="rounded-sm text-xs h-7 px-2" data-testid="mode-regie">Régie</Button>
                      <Button type="button" variant={regieMode === "lead" ? "default" : "outline"} onClick={() => setRegieMode("lead")} className="rounded-sm text-xs h-7 px-2" data-testid="mode-lead">Lead</Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">{regie.toLocaleString('fr-FR')} €</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-muted-foreground">Nombre de classes:</span>
                    <span className="ml-2 font-semibold">{scenario}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground mr-2">Coût total (dont régie):</span>
                    <span className="text-lg font-bold text-red-600">
                      {resultats?.totalCoutTravaux?.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Résultats TOTAUX */}
            <div className="border border-border rounded-sm bg-card p-6 space-y-5">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
                <TrendingUp className="w-5 h-5 text-primary" />
                Résultats - {nombreLogements} logement{nombreLogements > 1 ? 's' : ''}
              </h2>
              
              {resultats && (
                <div className="space-y-4">
                  {/* Total CA TTC */}
                  <div className="p-4 rounded-sm border border-primary/30 bg-primary/5">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">CA TTC Total</span>
                      <span className="text-xl font-bold text-primary">
                        {resultats.totalCaTTC.toLocaleString('fr-FR')} €
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {resultats.totalMwhCumac.toLocaleString('fr-FR')} MWh cumac × coef × {valorisation} €
                    </p>
                  </div>

                  {/* CA HT */}
                  <div className="p-3 rounded-sm bg-muted/50 border border-border">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">CA HT Total (TVA 5.5%)</span>
                      <span className="font-medium">{resultats.totalCaHT.toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>

                  {/* Coût travaux */}
                  <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-red-700">Coût travaux total</span>
                      <span className="font-medium text-red-700">-{resultats.totalCoutTravaux.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex justify-between items-center text-xs mt-1">
                      <span className="text-red-600/70">dont régie</span>
                      <span className="text-red-600/70">-{(regie * nombreLogements).toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>

                  {/* MARGE TOTALE */}
                  <div className={cn(
                    "p-5 rounded-sm border-2",
                    resultats.totalMarge >= 0 
                      ? "bg-emerald-500/10 border-emerald-500/40" 
                      : "bg-red-500/10 border-red-300"
                  )}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        {resultats.totalMarge >= 0 ? (
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                        <span className="font-semibold">MARGE TOTALE</span>
                      </div>
                      <span className={cn(
                        "text-2xl font-bold",
                        resultats.totalMarge >= 0 ? "text-emerald-700" : "text-red-700"
                      )}>
                        {resultats.totalMarge.toLocaleString('fr-FR')} €
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Moyenne par logement: {resultats.moyenneMarge.toLocaleString('fr-FR')} €
                    </p>
                  </div>

                  {/* Info */}
                  <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-sm text-xs text-muted-foreground">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Cliquez sur l'onglet "Logements" pour personnaliser la surface de chaque logement
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Onglet Logements */}
        <TabsContent value="logements">
          <div className="border border-border rounded-sm bg-card p-6">
            <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Détail des {nombreLogements} logements
              </h2>
              <div className="text-sm text-muted-foreground">
                Surface par défaut: <span className="font-semibold">{surfaceDefaut} m²</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-4 py-3 text-left font-semibold w-24">Logement</th>
                    <th className="px-4 py-3 text-center font-semibold">Surface (m²)</th>
                    <th className="px-4 py-3 text-center font-semibold">Coefficient</th>
                    <th className="px-4 py-3 text-right font-semibold">CA TTC</th>
                    <th className="px-4 py-3 text-right font-semibold">CA HT</th>
                    <th className="px-4 py-3 text-right font-semibold">Coût</th>
                    <th className="px-4 py-3 text-right font-semibold">Marge</th>
                    <th className="px-4 py-3 text-center font-semibold w-24">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {resultatsParLogement.map((logement, idx) => {
                    const isCustom = surfacesLogements[idx] !== null;
                    const isEditing = editingLogement === idx;
                    
                    return (
                      <tr key={idx} className={cn(
                        "hover:bg-muted/30",
                        isCustom && "bg-amber-500/10"
                      )}>
                        <td className="px-4 py-3 font-medium">
                          #{logement.index}
                          {isCustom && (
                            <span className="ml-2 text-xs text-amber-600">*</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <Input
                              type="number"
                              value={tempSurface}
                              onChange={(e) => setTempSurface(e.target.value)}
                              className="w-20 rounded-sm text-center mx-auto"
                              autoFocus
                            />
                          ) : (
                            <span className={cn(isCustom && "text-amber-700 font-semibold")}>
                              {logement.surface}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center tabular-nums">{logement.coefficient}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{logement.caTTC.toLocaleString('fr-FR')} €</td>
                        <td className="px-4 py-3 text-right tabular-nums">{logement.caHT.toLocaleString('fr-FR')} €</td>
                        <td className="px-4 py-3 text-right tabular-nums text-red-600">{logement.coutTravaux.toLocaleString('fr-FR')} €</td>
                        <td className={cn(
                          "px-4 py-3 text-right font-semibold tabular-nums",
                          logement.margeBrute >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {logement.margeBrute.toLocaleString('fr-FR')} €
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => saveLogementSurface(idx)}
                                className="h-7 w-7 p-0 text-emerald-600"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingLogement(null)}
                                className="h-7 w-7 p-0 text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditingLogement(idx)}
                                className="h-7 w-7 p-0"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {isCustom && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => resetLogementSurface(idx)}
                                  className="h-7 w-7 p-0 text-amber-600"
                                  title="Remettre surface par défaut"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-semibold">
                    <td className="px-4 py-3" colSpan={3}>TOTAL</td>
                    <td className="px-4 py-3 text-right">{resultats?.totalCaTTC?.toLocaleString('fr-FR')} €</td>
                    <td className="px-4 py-3 text-right">{resultats?.totalCaHT?.toLocaleString('fr-FR')} €</td>
                    <td className="px-4 py-3 text-right text-red-600">{resultats?.totalCoutTravaux?.toLocaleString('fr-FR')} €</td>
                    <td className={cn(
                      "px-4 py-3 text-right text-lg",
                      (resultats?.totalMarge || 0) >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {resultats?.totalMarge?.toLocaleString('fr-FR')} €
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {logementsPersonnalises > 0 && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm text-sm">
                <span className="text-amber-800">
                  <strong>*</strong> {logementsPersonnalises} logement{logementsPersonnalises > 1 ? 's ont une surface personnalisée' : ' a une surface personnalisée'}
                </span>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Onglet Coûts unitaires */}
        <TabsContent value="couts">
          <div className="border border-border rounded-sm bg-card p-6">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3 mb-6">
              <Settings className="w-5 h-5 text-primary" />
              Coûts unitaires (modifiables en cas de besoin)
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-4 py-3 text-left font-semibold">Travail</th>
                    <th className="px-4 py-3 text-right font-semibold">Matériel (€)</th>
                    <th className="px-4 py-3 text-right font-semibold">Pose (€)</th>
                    <th className="px-4 py-3 text-right font-semibold">Total (€)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Object.entries(TRAVAUX_DEFAUT).map(([key, travail]) => (
                    <tr key={key} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{travail.label}</td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          value={couts[key].materiel}
                          onChange={(e) => handleCoutChange(key, 'materiel', e.target.value)}
                          className="w-24 rounded-sm text-right ml-auto"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          value={couts[key].pose}
                          onChange={(e) => handleCoutChange(key, 'pose', e.target.value)}
                          className="w-24 rounded-sm text-right ml-auto"
                        />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">
                        {(couts[key].materiel + couts[key].pose).toLocaleString('fr-FR')} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <p className="text-sm text-amber-800">
                <strong>Note :</strong> Ces coûts sont utilisés pour calculer le coût total des travaux. 
                Modifiez-les si un chantier vous coûte plus cher que prévu.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Frise chronologique 174 */}
      {(() => {
        const { timelines, totalDays } = buildTimelinesForProgram({ programType: "174" });
        return <TimelineDisplay timelines={timelines} totalDays={totalDays} />;
      })()}
    </div>
  );
}
